/**
 * Envio do comunicado de suspensão — só para admin (o servidor também barra).
 *
 * A folha mostra, antes do disparo, as duas coisas que evitam arrependimento:
 * o e-mail exatamente como vai chegar (o HTML vem do servidor, da mesma função
 * que o worker usa para enviar) e o que acontece com cada placa escolhida —
 * quantas recebem, quantas não têm e-mail e quantas já foram avisadas há pouco.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  FlaskConical,
  Loader2,
  Mail,
  MailX,
  Send,
  XCircle,
} from 'lucide-react';
import { api } from '../../lib/api.ts';
import { numero } from '../../lib/format.ts';
import { Aviso, Botao, Esqueleto } from '../../components/ui/index.tsx';
import Folha from '../../components/ui/Folha.tsx';
import { dataBr, type Item } from './dados.ts';

interface Verificacao {
  janelaDias: number;
  aptos: number;
  recentes: { placa: string; nome: string | null; em: string | null }[];
  semEmail: { placa: string; nome: string | null }[];
  naoEncontradas: number;
}

interface Previa {
  assunto: string;
  html: string;
}

export default function FolhaComunicado({
  placas,
  aberta,
  onFechar,
  onEnviado,
}: {
  placas: string[];
  aberta: boolean;
  onFechar: () => void;
  onEnviado: (mensagem: string) => void;
}) {
  const qc = useQueryClient();
  const [incluirRecentes, setIncluirRecentes] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const chave = placas.join(',');
  const verificacao = useQuery({
    queryKey: ['comunicado', 'verificar', chave],
    queryFn: () => api.post<Verificacao>('/rastreamento/comunicado/verificar', { placas }),
    enabled: aberta && placas.length > 0,
    staleTime: 0,
  });
  // Sem placa selecionada (aberta só para testar), a prévia usa o exemplo.
  const previa = useQuery({
    queryKey: ['comunicado', 'previa', placas[0] ?? ''],
    queryFn: () =>
      api.get<Previa>(
        `/rastreamento/comunicado/previa${placas[0] ? `?placa=${encodeURIComponent(placas[0])}` : ''}`,
      ),
    enabled: aberta,
  });
  const soTeste = placas.length === 0;

  const enviar = useMutation({
    mutationFn: () =>
      api.post<{ enfileirados: number; semEmail: number; recentesIgnoradas: number }>(
        '/rastreamento/comunicado',
        { placas, incluirRecentes },
      ),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['rastreamento'] });
      const partes = [`${numero(r.enfileirados)} comunicado(s) na fila de envio`];
      if (r.semEmail) partes.push(`${numero(r.semEmail)} sem e-mail`);
      if (r.recentesIgnoradas) partes.push(`${numero(r.recentesIgnoradas)} já avisada(s), não reenviada(s)`);
      onEnviado(`${partes.join(' · ')}. O envio é espaçado e leva alguns minutos.`);
      setIncluirRecentes(false);
      setErro(null);
    },
    onError: (e) => setErro(e instanceof Error ? e.message : 'Não foi possível enviar.'),
  });

  const v = verificacao.data;
  const total = v ? v.aptos + (incluirRecentes ? v.recentes.length : 0) : 0;

  return (
    <Folha aberta={aberta} titulo="Comunicado de suspensão de cobertura" onFechar={onFechar} larga>
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_280px]">
        {/* Prévia */}
        <div className="min-w-0">
          <p className="mb-2 text-xs font-medium tracking-wide text-[var(--faint)] uppercase">
            Prévia{placas.length > 1 ? ` · ${placas[0]}` : ''}
          </p>
          {previa.data ? (
            <div className="overflow-hidden rounded-[var(--r-md)] border border-[var(--border)]">
              <p className="truncate border-b border-[var(--border)] bg-[var(--bg-subtle)] px-3 py-2 text-[13px] text-[var(--fg)]">
                <span className="text-[var(--muted)]">Assunto:</span> {previa.data.assunto}
              </p>
              {/* `sandbox` sem permissões: o HTML do e-mail não executa nada
                  dentro do Hub, mesmo vindo do nosso próprio servidor. */}
              <iframe
                title="Prévia do e-mail"
                sandbox=""
                srcDoc={previa.data.html}
                className="block h-[440px] w-full bg-white"
              />
            </div>
          ) : (
            <Esqueleto className="h-[480px]" />
          )}
        </div>

        {/* Resumo */}
        <div className="flex flex-col gap-3">
          <EnvioTeste placa={placas[0]} aberta={aberta} />

          {soTeste ? (
            <p className="text-[13px] text-[var(--muted)]">
              Para enviar aos associados, feche e selecione as placas na lista. O teste acima usa um
              associado de exemplo.
            </p>
          ) : (
          <>
          <p className="text-xs font-medium tracking-wide text-[var(--faint)] uppercase">
            Destinatários
          </p>
          {!v ? (
            <Esqueleto className="h-32" />
          ) : (
            <>
              <Linha
                icone={<Mail size={14} />}
                rotulo="Recebem o comunicado"
                valor={total}
                tom="var(--fg)"
              />
              {v.semEmail.length > 0 && (
                <Linha
                  icone={<MailX size={14} />}
                  rotulo="Sem e-mail no cadastro"
                  valor={v.semEmail.length}
                  tom="var(--danger)"
                  detalhe={v.semEmail.map((s) => s.placa).join(', ')}
                />
              )}
              {v.recentes.length > 0 && (
                <div className="rounded-[var(--r-md)] border border-[var(--border)] p-3">
                  <Linha
                    icone={<AlertTriangle size={14} />}
                    rotulo={`Avisadas nos últimos ${v.janelaDias} dias`}
                    valor={v.recentes.length}
                    tom="var(--warn)"
                    detalhe={v.recentes
                      .map((r) => `${r.placa} (${r.em ? dataBr(r.em.slice(0, 10)) : '—'})`)
                      .join(', ')}
                  />
                  <label className="mt-2 flex cursor-pointer items-start gap-2 text-[13px] text-[var(--fg)]">
                    <input
                      type="checkbox"
                      checked={incluirRecentes}
                      onChange={(e) => setIncluirRecentes(e.target.checked)}
                      className="mt-0.5 size-4 accent-[var(--fg)]"
                    />
                    Enviar de novo para estas também
                  </label>
                </div>
              )}

              <Aviso tom="alerta" icone={<AlertTriangle size={14} />}>
                O associado é informado de que as coberturas estão suspensas. Confira a seleção.
              </Aviso>

              {erro && <p className="text-[13px] text-[var(--danger)]">{erro}</p>}

              <Botao
                variante="primario"
                icone={<Send size={14} />}
                carregando={enviar.isPending}
                disabled={total === 0 || enviar.isPending}
                onClick={() => enviar.mutate()}
                larguraTotal
              >
                {total === 0
                  ? 'Nenhuma placa para enviar'
                  : `Enviar para ${numero(total)} ${total === 1 ? 'associado' : 'associados'}`}
              </Botao>
              <Botao variante="fantasma" onClick={onFechar} larguraTotal>
                Cancelar
              </Botao>
            </>
          )}
          </>
          )}
        </div>
      </div>
    </Folha>
  );
}

interface StatusTeste {
  status: 'pendente' | 'enviando' | 'enviado' | 'falhou';
  erro: string | null;
  email: string;
}

/**
 * Teste para um endereço qualquer, pela mesma fila e o mesmo SMTP do envio
 * real — é o que prova que o Stalwart entrega. A tela acompanha o status até
 * o worker responder, para o erro do servidor de e-mail aparecer aqui, e não
 * só no log.
 */
function EnvioTeste({ placa, aberta }: { placa?: string; aberta: boolean }) {
  const [email, setEmail] = useState('');
  const [id, setId] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const enviar = useMutation({
    mutationFn: () => api.post<{ id: string }>('/rastreamento/comunicado/teste', { email, placa }),
    onSuccess: (r) => {
      setId(r.id);
      setErro(null);
    },
    onError: (e) => setErro(e instanceof Error ? e.message : 'Não foi possível enviar o teste.'),
  });

  const status = useQuery({
    queryKey: ['comunicado', 'teste', id],
    queryFn: () => api.get<StatusTeste>(`/rastreamento/comunicado/teste/${id}`),
    enabled: aberta && id !== null,
    // O worker roda a cada minuto: consulta a cada 3s até sair da fila.
    refetchInterval: (q) =>
      q.state.data && ['enviado', 'falhou'].includes(q.state.data.status) ? false : 3000,
  });

  const s = status.data;
  const emAndamento = id !== null && (!s || s.status === 'pendente' || s.status === 'enviando');

  return (
    <div className="rounded-[var(--r-md)] border border-[var(--border)] bg-[var(--bg-subtle)] p-3">
      <p className="mb-2 text-xs font-medium tracking-wide text-[var(--faint)] uppercase">
        Enviar teste
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim()) enviar.mutate();
        }}
        className="flex flex-col gap-2"
      >
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="seu@email.com"
          aria-label="E-mail para o teste"
          className="anel-foco h-9 w-full rounded-[var(--r-md)] border border-[var(--border-strong)] bg-[var(--surface)] px-3 text-base text-[var(--fg)] placeholder:text-[var(--faint)] sm:text-[13px]"
        />
        <Botao
          type="submit"
          tamanho="sm"
          icone={<FlaskConical size={14} />}
          carregando={enviar.isPending}
          disabled={enviar.isPending || emAndamento}
          larguraTotal
        >
          Enviar teste
        </Botao>
      </form>

      {erro && <p className="mt-2 text-xs text-[var(--danger)]">{erro}</p>}
      {id && (
        <p
          className="mt-2 flex items-start gap-1.5 text-xs"
          style={{
            color:
              s?.status === 'enviado'
                ? 'var(--success)'
                : s?.status === 'falhou'
                  ? 'var(--danger)'
                  : 'var(--muted)',
          }}
        >
          {s?.status === 'enviado' ? (
            <>
              <CheckCircle2 size={13} className="mt-px shrink-0" /> Entregue ao servidor de e-mail.
              Confira a caixa de entrada (e o spam) de {s.email}.
            </>
          ) : s?.status === 'falhou' ? (
            <>
              <XCircle size={13} className="mt-px shrink-0" /> Falhou: {s.erro}
            </>
          ) : (
            <>
              <Loader2 size={13} className="mt-px shrink-0 animate-spin" /> Na fila — o envio sai
              em até 1 minuto.
            </>
          )}
        </p>
      )}
      <p className="mt-2 text-[11px] leading-relaxed text-[var(--faint)]">
        Sai com “[TESTE]” no assunto{placa ? `, com os dados de ${placa}` : ''}. Não marca a placa
        como avisada.
      </p>
    </div>
  );
}

function Linha({
  icone,
  rotulo,
  valor,
  tom,
  detalhe,
}: {
  icone: React.ReactNode;
  rotulo: string;
  valor: number;
  tom: string;
  detalhe?: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 text-[13px]">
        <span style={{ color: tom }}>{icone}</span>
        <span className="flex-1 text-[var(--fg)]">{rotulo}</span>
        <span className="num font-semibold" style={{ color: tom }}>
          {numero(valor)}
        </span>
      </div>
      {detalhe && (
        <p className="num mt-1 pl-6 text-xs break-words text-[var(--faint)]">{detalhe}</p>
      )}
    </div>
  );
}

/** Marca na lista: quando a placa foi avisada, ou se o envio falhou. */
export function MarcaComunicado({ item }: { item: Item }) {
  const c = item.comunicado;
  if (!c) return null;
  const quando = c.em ? new Date(c.em).toLocaleDateString('pt-BR') : '';
  const { texto, tom } =
    c.status === 'enviado'
      ? { texto: `avisado ${quando}`, tom: 'var(--muted)' }
      : c.status === 'falhou'
        ? { texto: 'falha no aviso', tom: 'var(--danger)' }
        : { texto: 'aviso na fila', tom: 'var(--warn)' };
  return (
    <span
      title={c.status === 'falhou' ? (c.erro ?? undefined) : undefined}
      className="mt-1 inline-flex items-center gap-1 rounded-[var(--r-full)] px-1.5 py-0.5 text-[10px] font-medium"
      style={{ color: tom, background: 'color-mix(in oklab, currentColor 12%, transparent)' }}
    >
      <Mail size={10} />
      {texto}
    </span>
  );
}
